import { useState, useEffect } from 'react';
import { aiContentAPI, featureFlagsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Shield,
  Loader2,
  Copy,
  Check,
  ClipboardPaste,
  ArrowRightLeft,
  AlertTriangle,
  Crown,
  Sparkles,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';
import FeatureGuard from '../components/FeatureGuard';

export default function BypassContent() {
  const { user, isAdmin } = useAuth();
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  const [stats, setStats] = useState(null);
  const [converting, setConverting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [featureFlag, setFeatureFlag] = useState(null);
  const [loading, setLoading] = useState(true);

  const isFeatureFree = (featureId) => {
    const isMonthly = user?.subscription_type === 'monthly';
    const isProtected = user?.protected_features?.includes(featureId);
    const isVIP = featureFlag?.is_vip;
    return isAdmin || (isMonthly && (isProtected || isVIP));
  };

  useEffect(() => {
    const fetchFlag = async () => {
      try {
        const res = await featureFlagsAPI.getAll();
        const flag = res.data.find(f => f.id === 'bypass_content');
        setFeatureFlag(flag);
      } catch (error) {
        console.error('Failed to fetch feature flag:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFlag();
  }, []);

  const handlePaste = async () => {
    try {
      // Try Clipboard API first
      if (navigator.clipboard && navigator.clipboard.readText) {
        const text = await navigator.clipboard.readText();
        if (text) {
          setInputText(text);
          setOutputText('');
          setStats(null);
          toast.success('Đã dán nội dung từ clipboard!');
          return;
        }
      }
    } catch {
      // Clipboard API failed, use fallback
    }
    
    // Fallback: focus textarea and exec paste, or prompt user
    try {
      const textarea = document.querySelector('[data-testid="input-textarea"]');
      if (textarea) {
        textarea.focus();
        const result = document.execCommand('paste');
        if (result) {
          toast.success('Đã dán! Kiểm tra nội dung.');
          return;
        }
      }
    } catch {
      // execCommand also failed
    }
    
    // Last resort: prompt manual paste
    toast.info('Nhấn Ctrl+V (hoặc Cmd+V) để dán nội dung vào ô bên dưới.', { duration: 4000 });
    const textarea = document.querySelector('[data-testid="input-textarea"]');
    if (textarea) textarea.focus();
  };

  const handleConvert = async () => {
    if (!inputText.trim()) {
      toast.error('Vui lòng nhập hoặc dán nội dung');
      return;
    }
    setConverting(true);
    setOutputText('');
    setStats(null);
    try {
      const res = await aiContentAPI.bypassText(inputText);
      setOutputText(res.data.bypassed);
      setStats({
        replaced: res.data.replaced_count,
        total: res.data.total_chars
      });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 
                       (!error.response ? 'Không thể kết nối đến máy chủ. Vui lòng kiểm tra lại kết nối mạng hoặc cấu hình CORS.' : 'Chuyển đổi thất bại');
      toast.error(errorMsg);
    } finally {
      setConverting(false);
    }
  };

  const handleCopyOutput = async () => {
    try {
      await navigator.clipboard.writeText(outputText);
      setCopied(true);
      toast.success('Đã copy!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copy thất bại');
    }
  };

  return (
    <FeatureGuard feature={featureFlag}>
      <div className="space-y-6" data-testid="bypass-page">
      {/* Header */}
      <div className="animate-fadeIn">
        <h1 className="text-2xl font-bold text-[#1C1E21] flex items-center gap-2">
          <ArrowRightLeft className="text-[#1877F2]" />
          Lách nội dung (Bypass)
          {featureFlag?.is_vip && (
            <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] uppercase font-bold rounded-full border border-amber-200 flex items-center gap-1">
              <Crown size={12} />
              VIP
            </span>
          )}
        </h1>
        <p className="text-[#606770] mt-1">
          Chuyển đổi ký tự để tránh bị Facebook quét vi phạm nội dung
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fadeIn stagger-1">
        {/* Input */}
        <Card data-testid="input-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold">Nội dung gốc</CardTitle>
                <CardDescription>Dán hoặc nhập nội dung cần lách</CardDescription>
              </div>
              <Button
                variant="outline"
                onClick={handlePaste}
                data-testid="paste-btn"
              >
                <ClipboardPaste size={16} className="mr-2" />
                Dán
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              value={inputText}
              onChange={(e) => { setInputText(e.target.value); setOutputText(''); setStats(null); }}
              rows={12}
              placeholder="Dán nội dung vào đây hoặc nhấn nút 'Dán' phía trên..."
              className="font-mono text-sm resize-none"
              data-testid="input-textarea"
            />
            <div className="flex items-center justify-between text-sm text-[#8C939D]">
              <span>{inputText.length} ký tự</span>
            </div>
            <Button
              onClick={handleConvert}
              disabled={converting || !inputText.trim() || (!isAdmin && featureFlag?.is_vip && user?.subscription_type !== 'monthly')}
              className="bg-[#1877F2] hover:bg-[#166FE5]"
              data-testid="bypass-button"
            >
              {converting ? (
                <><Loader2 size={18} className="animate-spin mr-2" />Đang xử lý...</>
              ) : (
                <><Sparkles size={18} className="mr-2" />
                {!isAdmin && featureFlag?.is_vip && user?.subscription_type !== 'monthly' ? (
                  "Dành cho VIP"
                ) : (
                  featureFlag?.is_vip ? "Lách ngay (VIP)" : (featureFlag?.credit_cost > 0 ? `Lách ngay (${featureFlag.credit_cost} credits)` : "Lách ngay (Miễn phí)")
                )}
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Output */}
        <Card className={outputText ? 'border-2 border-[#31A24C]' : ''} data-testid="output-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold">Kết quả đã lách</CardTitle>
                <CardDescription>Nội dung sau khi thay thế ký tự Unicode</CardDescription>
              </div>
              {outputText && (
                <Button
                  variant="outline"
                  onClick={handleCopyOutput}
                  className="border-[#31A24C] text-[#1E7132]"
                  data-testid="copy-output-btn"
                >
                  {copied ? <Check size={16} className="mr-2" /> : <Copy size={16} className="mr-2" />}
                  {copied ? 'Đã copy!' : 'Copy'}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              value={outputText}
              readOnly
              rows={12}
              placeholder="Kết quả sẽ hiển thị ở đây sau khi chuyển đổi..."
              className="font-mono text-sm resize-none bg-[#F7F8FA]"
              data-testid="output-textarea"
            />

            {/* Stats */}
            {stats && (
              <div className="p-4 bg-[#FEF9E6] border border-[#FFE69C] rounded-xl animate-fadeIn" data-testid="stats-box">
                <div className="flex items-center gap-3 mb-3">
                  <AlertTriangle size={20} className="text-[#8F6702]" />
                  <span className="font-semibold text-[#8F6702]">Kết quả chuyển đổi</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-white rounded-lg">
                    <div className="text-2xl font-bold text-[#8F6702]">{stats.replaced}</div>
                    <div className="text-xs text-[#606770] mt-1">Ký tự đã thay thế</div>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg">
                    <div className="text-2xl font-bold text-[#1C1E21]">{stats.total}</div>
                    <div className="text-xs text-[#606770] mt-1">Tổng ký tự</div>
                  </div>
                </div>
                <p className="text-xs text-[#8C939D] mt-3">
                  * Các ký tự Latin đã được thay bằng ký tự Unicode tương tự (Cyrillic, v.v.) để tránh bị phát hiện bởi hệ thống AI của Facebook.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Info */}
      <Card className="animate-fadeIn stagger-2">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-[#FEF9E6] flex items-center justify-center flex-shrink-0">
              <Shield size={20} className="text-[#8F6702]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#1C1E21] mb-1">Cách hoạt động</h3>
              <ul className="text-sm text-[#606770] space-y-1 list-disc list-inside">
                <li>Hệ thống thay thế các ký tự Latin (a, b, c...) bằng ký tự Unicode trông giống hệt (а Cyrillic, v.v.)</li>
                <li>Mắt người không phân biệt được, nhưng hệ thống AI sẽ không nhận diện từ vi phạm</li>
                <li>Chi phí: {featureFlag?.is_vip ? 'Tính năng VIP' : (featureFlag?.credit_cost > 0 ? `${featureFlag.credit_cost} credits/lần` : 'Hoàn toàn miễn phí')}</li>
                <li>Admin có thể tùy chỉnh bảng thay thế ký tự trong phần Quản trị</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
    </FeatureGuard>
  );
}
